import React, {
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';
import { useSeriesOptions } from '../../utils/useSeriesOptions';
import { InstrumentPill, ValuePill, FromIndicatorBadge } from './IndicatorPreviewFields';

type RangeType = 'high-low' | 'open-close';

const RANGE_LABELS: Record<RangeType, string> = {
  'high-low': 'High - Low',
  'open-close': 'Open - Close',
};

export type IndicatorEditorBarRangeRef = {
  getConfig: () => {
    instrumentId: string;
    rangeType: RangeType;
  };
};

type Props = {
  config: {
    instrumentId: string;
    rangeType?: RangeType;
  };
  onChange: (config: Props['config']) => void;
  editMode: boolean;
  indicatorId?: string;
};

const IndicatorEditorBarRange = forwardRef<IndicatorEditorBarRangeRef, Props>(
  ({ config, onChange, editMode, indicatorId }, ref) => {
    // Use unified series options
    const allSeriesOptions = useSeriesOptions(indicatorId);

    // Filter to only series that support at least one valid range
    const validSeriesOptions = allSeriesOptions.filter(series => {
      const fields = series.emittedFields;
      const hasHighLow = 'High' in fields && fields.High === 'Number' &&
        'Low' in fields && fields.Low === 'Number';
      const hasOpenClose = 'Open' in fields && fields.Open === 'Number' &&
        'Close' in fields && fields.Close === 'Number';
      return hasHighLow || hasOpenClose;
    });

    const hasSingleOption = validSeriesOptions.length === 1;

    // Auto-select if only one valid series
    useEffect(() => {
      if (hasSingleOption && config.instrumentId !== validSeriesOptions[0].id) {
        onChange({ ...config, instrumentId: validSeriesOptions[0].id });
      }
    }, [hasSingleOption, validSeriesOptions, config.instrumentId, onChange]);

    // Find selected series
    const selectedSeries = validSeriesOptions.find(s => s.id === config.instrumentId);
    const fields = selectedSeries?.emittedFields || {};

    // Determine which range types are available
    const canHighLow = 'High' in fields && fields.High === 'Number' &&
      'Low' in fields && fields.Low === 'Number';
    const canOpenClose = 'Open' in fields && fields.Open === 'Number' &&
      'Close' in fields && fields.Close === 'Number';

    // Current rangeType with smart fallback
    const currentRangeType: RangeType = (() => {
      if (!config.rangeType) return canHighLow ? 'high-low' : 'open-close';
      if (config.rangeType === 'high-low' && canHighLow) return 'high-low';
      if (config.rangeType === 'open-close' && canOpenClose) return 'open-close';
      return canHighLow ? 'high-low' : 'open-close';
    })();

    // Sync rangeType when series changes
    useEffect(() => {
      if (config.rangeType !== currentRangeType) {
        onChange({ ...config, rangeType: currentRangeType });
      }
    }, [currentRangeType, config, onChange]);

    const handleSeriesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange({ ...config, instrumentId: e.target.value });
    };

    const handleRangeTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange({ ...config, rangeType: e.target.value as RangeType });
    };

    useImperativeHandle(ref, () => ({
      getConfig: () => ({
        instrumentId: config.instrumentId,
        rangeType: currentRangeType,
      }),
    }));

    // Preview mode
    if (!editMode) {
      const label = selectedSeries?.label || '???';
      const rangeLabel = RANGE_LABELS[currentRangeType];
      return (
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <InstrumentPill>{label}</InstrumentPill>
          <ValuePill label="Range" value={rangeLabel} />
          {selectedSeries?.type === 'indicator' && <FromIndicatorBadge />}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-4 items-center">
        {/* Series selector */}
        {!hasSingleOption && validSeriesOptions.length > 0 && (
          <label>
            Series:
            <select
              value={config.instrumentId}
              onChange={handleSeriesChange}
              className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
            >
              {validSeriesOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label} {opt.type === 'indicator' ? ' (indicator)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Show message if no valid series */}
        {validSeriesOptions.length === 0 && (
          <span className="text-error text-sm">
            No series available with High/Low or Open/Close
          </span>
        )}

        {/* Range Type selector – only show valid options */}
        {(canHighLow || canOpenClose) && (
          <label>
            Range Type:
            <select
              value={currentRangeType}
              onChange={handleRangeTypeChange}
              className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
            >
              {canHighLow && <option value="high-low">High - Low</option>}
              {canOpenClose && <option value="open-close">Open - Close</option>}
            </select>
          </label>
        )}
      </div>
    );
  }
);

IndicatorEditorBarRange.displayName = 'IndicatorEditorBarRange';

export default IndicatorEditorBarRange;